import {
  HttpBackend,
  HttpErrorResponse,
  HttpEvent,
  HttpHeaders,
  HttpInterceptorFn,
  HttpRequest,
} from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { ENVIRONMENT } from '@vendorhub/shared/config';
import { Observable, catchError, firstValueFrom, switchMap, throwError, timer } from 'rxjs';
import { ApiError } from '../errors/api-error';
import { RefreshResponseSchema } from '../schemas/identity.schema';
import { TokenStore } from '../tokens/token.store';

/**
 * Module-scoped singleton so concurrent 401s share a single in-flight
 * refresh. The first failing request triggers `/identity/refresh`; others
 * queue on the same promise and retry once it resolves.
 */
let refreshInFlight: Promise<boolean> | null = null;

export const errorInterceptor: HttpInterceptorFn = (req, next) => {
  const tokens = inject(TokenStore);
  const router = inject(Router);
  const backend = inject(HttpBackend);
  const apiBaseUrl = inject(ENVIRONMENT).apiBaseUrl;

  return next(req).pipe(
    catchError((err: unknown) => {
      if (!(err instanceof HttpErrorResponse)) {
        return throwError(() => normalize(err));
      }

      if (err.status === 401 && !isAuthEndpoint(req)) {
        return handle401(req, next, tokens, router, backend, apiBaseUrl);
      }

      if (err.status === 429) {
        const delayMs = parseRetryAfter(err.headers.get('Retry-After'));
        if (delayMs !== null) {
          return timer(delayMs).pipe(switchMap(() => next(req)));
        }
        return throwError(() => toApiError(err));
      }

      return throwError(() => toApiError(err));
    }),
  );
};

function handle401(
  req: HttpRequest<unknown>,
  next: (r: HttpRequest<unknown>) => Observable<HttpEvent<unknown>>,
  tokens: TokenStore,
  router: Router,
  backend: HttpBackend,
  apiBaseUrl: string,
): Observable<HttpEvent<unknown>> {
  if (!tokens.refreshToken()) {
    tokens.clear();
    void router.navigate(['/auth/login']);
    return throwError(() =>
      toApiError(new HttpErrorResponse({ status: 401, statusText: 'Unauthorized' })),
    );
  }

  const pending = (refreshInFlight ??= refreshAccessToken(tokens, backend, apiBaseUrl));

  return new Observable<HttpEvent<unknown>>((subscriber) => {
    pending
      .then((ok) => {
        if (!ok) {
          tokens.clear();
          void router.navigate(['/auth/login']);
          subscriber.error(
            toApiError(new HttpErrorResponse({ status: 401, statusText: 'Unauthorized' })),
          );
          return;
        }
        next(req).subscribe(subscriber);
      })
      .catch((e) => subscriber.error(normalize(e)))
      .finally(() => {
        refreshInFlight = null;
      });
  });
}

/**
 * Calls `/identity/refresh` directly through `HttpBackend` to bypass the
 * interceptor chain (no recursion, no import cycle with shared/auth).
 */
async function refreshAccessToken(
  tokens: TokenStore,
  backend: HttpBackend,
  apiBaseUrl: string,
): Promise<boolean> {
  const refreshToken = tokens.refreshToken();
  if (!refreshToken) return false;

  const request = new HttpRequest(
    'POST',
    `${apiBaseUrl}/api/v1/identity/refresh`,
    { refreshToken },
    {
      headers: new HttpHeaders({ 'Content-Type': 'application/json' }),
      withCredentials: true,
      responseType: 'json',
    },
  );

  try {
    const event = await firstValueFrom(backend.handle(request));
    // `HttpBackend.handle` emits a final `HttpResponse`; earlier events
    // (sent/upload) are filtered by firstValueFrom only if we take the
    // last — we take whichever comes first that is not sent-indicator.
    // In practice for `responseType: 'json'` with a simple POST, the
    // single emitted event is the final response.
    const body = (event as { body?: unknown }).body;
    const parsed = RefreshResponseSchema.safeParse(body);
    if (!parsed.success) return false;
    tokens.set(
      parsed.data.data.accessToken,
      parsed.data.data.expiresIn,
      parsed.data.data.refreshToken,
    );
    return true;
  } catch {
    return false;
  }
}

function isAuthEndpoint(req: HttpRequest<unknown>): boolean {
  return /\/identity\/(login|refresh|register|send-|verify-)/.test(req.url);
}

function parseRetryAfter(value: string | null): number | null {
  if (!value) return null;
  const seconds = Number(value);
  if (!Number.isNaN(seconds)) return seconds * 1000;
  const date = Date.parse(value);
  if (!Number.isNaN(date)) return Math.max(0, date - Date.now());
  return null;
}

function toApiError(err: HttpErrorResponse): ApiError {
  const body = err.error as Record<string, unknown> | undefined;
  const envelope =
    body && typeof body === 'object' && 'error' in body
      ? (body['error'] as { code?: string; message?: string; details?: unknown })
      : undefined;
  const meta =
    body && typeof body === 'object' && 'meta' in body
      ? (body['meta'] as { retryAfterSeconds?: number } | undefined)
      : undefined;
  return {
    status: err.status,
    message: envelope?.message ?? (body?.['message'] as string) ?? err.message ?? 'Request failed',
    code: envelope?.code ?? (body?.['code'] as string | undefined),
    correlationId: err.headers.get('X-Correlation-Id') ?? undefined,
    details: envelope?.details ?? body,
    retryAfterSeconds:
      meta?.retryAfterSeconds ?? parseRetryAfterSeconds(err.headers.get('Retry-After')),
  };
}

function parseRetryAfterSeconds(value: string | null): number | undefined {
  if (!value) return undefined;
  const n = Number(value);
  return Number.isNaN(n) ? undefined : n;
}

function normalize(err: unknown): ApiError {
  if (err instanceof HttpErrorResponse) return toApiError(err);
  return {
    status: 0,
    message: err instanceof Error ? err.message : 'Unknown error',
    details: err,
  };
}
